import { installmentScheduleTypeConstants } from "constants/account";
import { orderTypeConstants } from "constants/order";
import dayjs from "dayjs";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import { addMonthsLikeBackend } from "pages/OrderOverview/components/Overview/helpers";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";

export const StyledPriceContainer = styled.div`
  p {
    color: #706f7b;
  }
  .highlighted {
    color: #17143f;
  }
`;

export const HorizontalLine = styled.hr`
  border-top: 1px solid #e2e2e4;
`;

export function PaymentPlanDetails({
  accountData,
  estimatedCost,
  initialPayment,
}: {
  accountData: IAccountResponse;
  estimatedCost: number;
  initialPayment: number;
}) {
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const minDownPaymentTypeField = watch("paymentPlanMinDownPaymentType");
  const minDownPaymentPercentField = watch("paymentPlanMinDownPaymentPercent");
  const flatAmountField = watch("paymentPlanMinDownPaymentFlat");
  const paymentPlanDurationField = watch("paymentPlanDuration");
  const paymentPlanDurationFullField = watch("paymentPlanDurationFull");
  const dateOfServiceField = watch("dateOfService");

  const monthlyPayment = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    return Math.floor((remainingAmount / paymentPlanDurationField) * 100) / 100;
  }, [estimatedCost, initialPayment, paymentPlanDurationField]);

  const paymentsTimeline = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    const lastPayment =
      remainingAmount - monthlyPayment * (paymentPlanDurationField - 1);
    const { installmentScheduleType } = accountData.data;
    const isTwoWeekSchedule =
      installmentScheduleType ===
      installmentScheduleTypeConstants.TWO_WEEKS_BEFORE;
    let startDate: dayjs.Dayjs;
    let addAMonth = true;
    const parsedDate = dateOfServiceField
      ? dayjs.utc(dateOfServiceField, "MM/DD/YYYY", true)
      : dayjs.utc();
    const normalizedDateOfService = parsedDate.isValid()
      ? parsedDate.toISOString()
      : dayjs.utc().toISOString();
    if (isTwoWeekSchedule) {
      const twoWeeksBefore = dayjs
        .utc(normalizedDateOfService)
        .subtract(14, "days");
      const now = dayjs.utc();
      startDate = twoWeeksBefore.isBefore(now) ? dayjs() : twoWeeksBefore;
      addAMonth = !!twoWeeksBefore.isBefore(now);
    } else {
      startDate = dayjs();
    }
    // Convert startDate to a native Date once, then do backend-style setMonth math
    const startNativeDate = startDate.toDate();

    const installments = Array.from(
      { length: paymentPlanDurationField },
      (_, index) => {
        const updatedIndex = addAMonth ? index + 1 : index;
        const dueNative = addMonthsLikeBackend(startNativeDate, updatedIndex);
        const date = dayjs(dueNative).format("YYYY-MM-DD");
        const payment =
          index === paymentPlanDurationField - 1 ? lastPayment : monthlyPayment;

        return { date, payment };
      }
    );
    const initialPaymentObj = {
      date: "Due Today",
      payment: initialPayment,
    };
    return [initialPaymentObj, ...installments];
  }, [
    accountData,
    initialPayment,
    monthlyPayment,
    paymentPlanDurationField,
    dateOfServiceField,
  ]);

  const formIsNotValid = useMemo(() => {
    if (!dateOfServiceField) {
      return true;
    }
    if (
      paymentPlanDurationFullField === null ||
      paymentPlanDurationFullField === undefined
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Percentage" &&
      (minDownPaymentPercentField === null ||
        minDownPaymentPercentField === undefined)
    ) {
      return true;
    }
    if (
      minDownPaymentTypeField === "Flat amount" &&
      (flatAmountField === null || flatAmountField === undefined)
    ) {
      return true;
    }
    return false;
  }, [
    paymentPlanDurationFullField,
    minDownPaymentTypeField,
    minDownPaymentPercentField,
    flatAmountField,
    dateOfServiceField,
  ]);

  if (formIsNotValid) {
    return null;
  }
  return (
    <StyledPriceContainer className="flex flex-column gap-3 border-1 border-round-lg p-3 border-gray-300">
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-2">
        <p className="text-md highlighted font-semibold line-height-3">
          Due Today
        </p>
        <p className="text-lg text-primary font-semibold text-right line-height-1">
          {parsePrice(centsToDollars(initialPayment))}
        </p>
      </div>
      <HorizontalLine />
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-3">
        <div className="flex flex-column gap-1 sm:align-items-start align-items-center">
          <p className="text-base font-medium line-height-3 highlighted">
            + {parsePrice(centsToDollars(monthlyPayment))} monthly payments
          </p>
          <p className="font-normal text-sm line-height-3">
            {paymentPlanDurationField} payments
          </p>
        </div>
        <div className="flex flex-column gap-1">
          <p className="font-normal text-sm line-height-3 flex align-items-center">
            <i
              className="pi pi-calendar text-primary"
              style={{ fontSize: "14px", marginRight: "4px" }}
            />
            Last payment
          </p>
          <p className="text-sm font-semibold line-height-3 text-primary text-right">
            {paymentsTimeline[paymentsTimeline.length - 1].date}
          </p>
        </div>
      </div>
      <HorizontalLine />
      <div className="flex justify-content-between align-items-center w-full sm:flex-row flex-column gap-2">
        <p className="font-normal text-sm line-height-3">No interest</p>
        <p className="font-normal text-sm line-height-3">
          Total Cost{" "}
          <span className="text-primary font-semibold text-md">
            {parsePrice(centsToDollars(estimatedCost))}
          </span>
        </p>
      </div>
    </StyledPriceContainer>
  );
}
